import { chain } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import * as t from "io-ts";
import { NumberFromString } from "io-ts-types/lib/NumberFromString";

import { getReactivatedContext } from "../utils/environment";
import { notEmpty } from "../utils/functional";
import { sortProductVariants } from "../utils/sorting";
import { FinancingPlans } from "./financing";
import {
    BasketLineAPIURL,
    BasketLineID,
    IProductAPIURL,
    IProductID,
    ImageURL,
    ProductAPIURL,
    ProductBundlesListAPIURL,
    ProductCategoryAPIURL,
    ProductCategoryID,
    ProductClassAPIURL,
    ProductID,
    ProductImageID,
    ProductListAPIURL,
    ProductSKU,
    ProductUPC,
    ProductUUID,
    ReviewsBrandID,
    ReviewsProductID,
    ReviewsProductTypeID,
    ReviewsProductVariantID,
    SafeHTML,
    WebPageURL,
    isoImageURL,
    isoProductCategoryAPIURL,
    isoProductCategoryID,
    isoProductClassAPIURL,
} from "./nominals";
import { Price } from "./prices";
import { StringFromBoolean, nullable } from "./utils";

// ============================================================================
// Product Options
// ============================================================================

export const OptionCode = t.keyof({
    option_advanced_pressure_relief: null,
    option_advanced_support: null,
    option_chill: null,
    option_color: null,
    option_color_hex: null,
    option_coolingfeel: null,
    option_feel: null,
    option_height: null,
    option_level: null,
    option_model: null,
    option_profile: null,
    option_size: null,
    option_top: null,
});
export type IOptionCode = t.TypeOf<typeof OptionCode>;

export const OptionSingleValue = t.string;
export type IOptionSingleValue = t.TypeOf<typeof OptionSingleValue>;

const OptionMultiValue = t.readonlyArray(OptionSingleValue);
export type IOptionMultiValue = t.TypeOf<typeof OptionMultiValue>;

export const OptionValue = t.union([OptionSingleValue, OptionMultiValue]);
export type IOptionValue = t.TypeOf<typeof OptionValue>;

export type IOptionValues = { [K in IOptionCode]?: IOptionValue };

export type IOptionInputType =
    | "dropdown"
    | "radio"
    | "dropdown-with-pricing"
    | "radio-with-pricing";

const OptionLabel = t.readonly(
    t.intersection([
        t.type({
            label: t.string,
            values: t.readonlyArray(t.string),
        }),
        t.partial({
            description: t.string,
        }),
    ]),
);
export type IOptionLabel = t.TypeOf<typeof OptionLabel>;

// ============================================================================
// Product Attributes
// ============================================================================
const JSONAttributeValue = <T extends t.Mixed>(shape: T) => {
    const AttributeValue = new t.Type<t.TypeOf<T>, string, unknown>(
        "AttributeValue",
        shape.is,
        (val, context) => {
            return pipe(
                t.string.validate(val, context),
                chain((str) => {
                    try {
                        return t.success(JSON.parse(str));
                    } catch {
                        return t.failure(str, context);
                    }
                }),
            );
        },
        (data) => {
            return JSON.stringify(data);
        },
    );
    return AttributeValue;
};

export type IProductCategory = Readonly<{
    name: string;
    value: string;
}>;

const ProductAttribute = <T extends t.Mixed>(valueCodec: T) => {
    return t.readonly(
        t.intersection([
            t.type({
                name: t.string,
                code: t.string,
                value: valueCodec,
            }),
            t.partial({
                // All locale translations keyed by language code, e.g. {"en": "Soft", "fr-ca": "Mi-Ferme"}
                display_names: t.union([t.record(t.string, t.string), t.null]),
            }),
        ]),
    );
};

const JSONProductAttribute = <T extends t.Mixed>(valueCodec: T) => {
    return ProductAttribute(JSONAttributeValue(valueCodec));
};

const StringAttribute = ProductAttribute(t.string);
export type IStringAttribute = t.TypeOf<typeof StringAttribute>;

const NullableStringAttribute = ProductAttribute(nullable(t.string));

const MultiStringAttribute = ProductAttribute(t.readonlyArray(t.string));

const SingleOrMultiStringAttribute = t.union([
    StringAttribute,
    MultiStringAttribute,
]);

const NumberAttribute = ProductAttribute(t.union([t.number, NumberFromString]));

const StringFromBooleanAttribute = ProductAttribute(StringFromBoolean);

const BooleanAttribute = ProductAttribute(t.boolean);

const ProductOptionsAttribute = JSONProductAttribute(
    t.readonlyArray(OptionCode),
);

const ProductOptionInputTypesAttribute = JSONProductAttribute(
    t.record(
        t.string,
        t.union([
            t.literal("dropdown"),
            t.literal("radio"),
            t.literal("dropdown-with-pricing"),
            t.literal("radio-with-pricing"),
        ]),
    ),
);

const ProductOptionLabelsAttribute = JSONProductAttribute(
    t.record(t.string, t.readonlyArray(OptionLabel)),
);

const AnyAttribute = t.union([
    StringAttribute,
    NullableStringAttribute,
    MultiStringAttribute,
    NumberAttribute,
    BooleanAttribute,
    ProductOptionsAttribute,
    ProductOptionInputTypesAttribute,
    ProductOptionLabelsAttribute,
]);

const KnownProductAttributes = t.readonly(
    t.partial({
        product_options: ProductOptionsAttribute,
        product_option_input_types: ProductOptionInputTypesAttribute,
        product_option_labels: ProductOptionLabelsAttribute,
        option_advanced_pressure_relief: StringFromBooleanAttribute,
        option_advanced_support: StringFromBooleanAttribute,
        option_chill: StringFromBooleanAttribute,
        option_color: StringAttribute,
        option_color_hex: StringAttribute,
        option_coolingfeel: StringAttribute,
        option_feel: SingleOrMultiStringAttribute,
        option_height: StringAttribute,
        option_level: StringAttribute,
        option_model: StringAttribute,
        option_profile: StringAttribute,
        option_size: StringAttribute,
        option_top: StringAttribute,

        // SKU sent to CCH when computing taxes
        cch_product_sku: StringAttribute,

        // Other misc product data
        // all_position_sleeper: BooleanAttribute,
        back_sleeper: BooleanAttribute,
        // best_seller: BooleanAttribute,
        brand_color: StringAttribute,
        // clearance: BooleanAttribute,
        // closeout: BooleanAttribute,
        // cooling: BooleanAttribute,
        feel: StringAttribute,
        // highest_rated: BooleanAttribute,
        in_store_only: BooleanAttribute,
        product_callout: StringAttribute,
        // product_model: StringAttribute,
        // promo_badge: StringAttribute,
        // side_sleeper: BooleanAttribute,
        // stomach_sleeper: BooleanAttribute,
        // travel: BooleanAttribute,
        // warranty: StringAttribute,
        upgrade_option_title: StringAttribute,
        firmness_rank: NumberAttribute,
        budget_rank: NumberAttribute,
        configurator_root_product_selector_icon: NullableStringAttribute,
    }),
);

const OtherProductAttributes = t.record(
    t.string,
    t.union([
        ProductAttribute(t.readonlyArray(OptionCode)),
        ProductAttribute(
            t.record(
                t.string,
                t.union([
                    t.literal("dropdown"),
                    t.literal("radio"),
                    t.literal("dropdown-with-pricing"),
                    t.literal("radio-with-pricing"),
                ]),
            ),
        ),
        ProductAttribute(t.record(t.string, t.readonlyArray(OptionLabel))),
        StringAttribute,
        NullableStringAttribute,
        MultiStringAttribute,
        NumberAttribute,
        BooleanAttribute,
        t.undefined,
    ]),
);

const ProductAttributes = t.readonly(
    t.intersection([KnownProductAttributes, OtherProductAttributes]),
);
export type IProductAttributes = t.TypeOf<typeof ProductAttributes>;

const ProductAttributesFromArray = new t.Type<
    t.TypeOf<typeof ProductAttributes>,
    string,
    unknown
>(
    "ProductAttributesFromArray",
    ProductAttributes.is,
    (val, context) => {
        return pipe(
            t.array(AnyAttribute).validate(val, context),
            chain((attrList) => {
                try {
                    const attrs: {
                        [code: string]: t.TypeOf<typeof AnyAttribute>;
                    } = {};
                    for (const attr of attrList) {
                        attrs[attr.code] = attr;
                    }
                    return ProductAttributes.validate(attrs, context);
                } catch {
                    return t.failure(attrList, context);
                }
            }),
        );
    },
    (data) => {
        return JSON.stringify(Object.values(data));
    },
);

// ============================================================================
// Product Class
// ============================================================================
const ProductClass = t.readonly(
    t.type({
        id: t.number,
        url: ProductClassAPIURL,
        name: t.string,
        slug: t.string,
        requires_shipping: t.boolean,
        track_stock: t.boolean,
        products: t.string,
    }),
);

const ProductAttributeWithDescription = t.readonly(
    t.type({
        id: t.number,
        option: t.string,
        description: nullable(t.string),
        icon_image_url: nullable(ImageURL),
    }),
);

const ProductAttributeOptionGroup = t.readonly(
    t.type({
        id: t.number,
        name: t.string,
        options: t.readonlyArray(ProductAttributeWithDescription),
    }),
);
export type IProductAttributeOptionGroup = t.TypeOf<
    typeof ProductAttributeOptionGroup
>;

const ProductAttributeOptionGroups = t.readonly(
    t.type({
        id: t.number,
        name: t.string,
        code: t.string,
        type: t.string,
        required: t.boolean,
        option_group: nullable(ProductAttributeOptionGroup),
    }),
);

/**
 * See serializer: tsicommon.api.serializers.catalogue.VerboseProductClassSerializer
 */
export const VerboseProductClass = t.readonly(
    t.intersection([
        ProductClass,
        t.type({
            products: ProductListAPIURL,
            attributes: t.readonlyArray(ProductAttributeOptionGroups),
        }),
    ]),
);
export type IVerboseProductClass = t.TypeOf<typeof VerboseProductClass>;

// ============================================================================
// Misc Product Sub Objects
// ============================================================================
const ProductImageRole = t.keyof({
    "Product Tile": null,
    "Product Feed": null,
    "PLP Config": null,
    "Product Feed Additional": null,
});

const Image = t.readonly(
    t.intersection([
        t.type({
            role: ProductImageRole,
            original: ImageURL,
        }),
        t.partial({
            id: ProductImageID,
            caption: t.string,
            display_order: t.number,
            date_created: t.string,
            product: ProductID,
        }),
    ]),
);

const ValueProp = t.readonly(
    t.intersection([
        t.type({
            name: t.string,
            description: t.string,
        }),
        t.partial({
            image_url: ImageURL,
        }),
    ]),
);
export type IValueProp = t.TypeOf<typeof ValueProp>;

const ProductAvailability = t.readonly(
    t.intersection([
        t.type({
            is_available_to_buy: t.boolean,
            code: t.string,
            short_message: t.string,
            message: t.string,
        }),
        t.partial({
            num_available: t.number,
        }),
    ]),
);

// ============================================================================
// Editorial Badge — curated chip rendered on PDP / product tiles. Named so
// the derived IEditorialBadge type can be imported by consumers (e.g. the
// pillow grid) without re-declaring the shape. The codec itself stays
// internal; export it if a future consumer needs runtime decoding.
// ============================================================================
const EditorialBadge = t.readonly(
    t.type({
        display_label: t.string,
        description: t.string,
    }),
);
export type IEditorialBadge = t.TypeOf<typeof EditorialBadge>;

// ============================================================================
// Products. This is split into multiple types for a few reasons: (1) products
// are recursive (containing their parent and a list child children); (2) we add
// a few extra fields that aren't in the API, like the product_class_slug.
// ============================================================================
const APIBaseProduct = t.readonly(
    t.intersection([
        t.type({
            id: ProductID,
            url: ProductAPIURL,
            product_class: ProductClassAPIURL,
            categories: t.readonlyArray(ProductCategoryAPIURL),
            category_names: t.readonlyArray(t.string),
            attributes: ProductAttributesFromArray,
            images: t.readonlyArray(Image),
            price: Price,
            availability: ProductAvailability,
            title: t.string,
            slug: t.string,
            description: SafeHTML,
            bundles: ProductBundlesListAPIURL,
            link: nullable(WebPageURL),
            uuid: nullable(ProductUUID),
            upc: ProductUPC,
            rating: nullable(t.number),
            num_reviews: t.number,
            reviews_brand_id: nullable(ReviewsBrandID),
            reviews_product_type_id: nullable(ReviewsProductTypeID),
            reviews_product_id: nullable(ReviewsProductID),
            reviews_product_variant_ids: t.readonlyArray(
                ReviewsProductVariantID,
            ),
            subtitle: nullable(t.string),
        }),
        t.partial({
            alternate_links: t.readonlyArray(
                t.readonly(
                    t.type({
                        type: t.string,
                        link: WebPageURL,
                    }),
                ),
            ),
            show_in_site_search: t.boolean,
            value_props: t.readonlyArray(ValueProp),
            skus: t.readonlyArray(ProductSKU),
            enable_predicted_delivery_date: t.boolean,
            date_created: t.string,
            date_updated: t.string,
            // TEMPORARY: Union type to handle both old (string | null) and new (array) formats
            // After all cached responses expire (~24-48 hours after deployment), this can be simplified to just the array type
            promo_callout: t.union([
                // Old format (can be removed after cache expires)
                nullable(t.string),
                // New format
                t.readonlyArray(
                    t.readonly(
                        t.type({
                            text: t.string,
                            start_date: nullable(t.string),
                            end_date: nullable(t.string),
                        }),
                    ),
                ),
            ]),
            pricing_disclaimer: nullable(
                t.readonly(
                    t.type({
                        show_disclaimer: t.boolean,
                        tooltip_text: t.string,
                        last_full_price_date: nullable(t.string),
                    }),
                ),
            ),
            editorial_badge: nullable(EditorialBadge),
            // Attributes for cache TTL debugging
            _cache_now: t.number,
            _cache_expires: t.number,
            _cache_built: t.number,
        }),
    ]),
);

const BaseProduct = t.readonly(
    t.intersection([
        APIBaseProduct,
        t.type({
            skus: t.readonlyArray(ProductSKU),
            product_class_slug: t.string,
            category_ids: t.readonlyArray(ProductCategoryID),
            enable_predicted_delivery_date: t.boolean,
        }),
    ]),
);

type APIBaseProduct = t.TypeOf<typeof APIBaseProduct>;
type BaseProduct = t.TypeOf<typeof BaseProduct>;

interface APIProduct extends APIBaseProduct {
    readonly parent: APIProduct | IProductAPIURL | null;
    readonly children: ReadonlyArray<APIProduct>;
}

export interface InternalProduct extends BaseProduct {
    readonly parent: InternalProduct | null;
    readonly children: ReadonlyArray<InternalProduct>;
}

const APIProduct: t.Type<APIProduct> = t.recursion("APIProduct", () => {
    const _type: t.Type<APIProduct> = t.readonly(
        t.intersection([
            APIBaseProduct,
            t.type({
                parent: t.union([APIProduct, ProductAPIURL, t.null]),
                children: t.readonlyArray(APIProduct),
            }),
        ]),
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    ) as any;
    return _type;
});

const InternalProduct: t.Type<InternalProduct> = t.recursion(
    "InternalProduct",
    () => {
        const _type: t.Type<InternalProduct> = t.readonly(
            t.intersection([
                BaseProduct,
                t.type({
                    parent: nullable(InternalProduct),
                    children: t.readonlyArray(InternalProduct),
                }),
            ]),
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        ) as any;
        return _type;
    },
);

const convertProductFromAPI = (apiProduct: APIProduct): InternalProduct => {
    // Build parent
    const parent: InternalProduct["parent"] =
        apiProduct.parent && !ProductAPIURL.is(apiProduct.parent)
            ? convertProductFromAPI(apiProduct.parent)
            : null;
    // Get the product class slug
    const productClassSlug = isoProductClassAPIURL
        .unwrap(apiProduct.product_class)
        .split("/")
        .slice(-2)[0];
    // Always use the parent's rating
    const rating = parent ? parent.rating : apiProduct.rating;
    // Add dummy image if needed
    const hasDirectImgs = apiProduct.images && apiProduct.images.length;
    const hasParentImgs = parent && parent.images && parent.images.length > 0;
    // Build the placeholder image src from the reactivated STATIC_URL context
    // (not a Vite asset import, which produces a broken URL during SSR --
    // reactivated#353). This is a model, not a component, so read the context
    // directly rather than via the useStaticURL hook.
    const staticURL = getReactivatedContext()?.STATIC_URL ?? "/static/";
    const images: InternalProduct["images"] =
        !hasDirectImgs && !hasParentImgs
            ? [
                  ...apiProduct.images,
                  {
                      original: isoImageURL.wrap(
                          `${staticURL}img/products/default-square.png`,
                      ),
                      role: "Product Tile",
                  },
              ]
            : apiProduct.images;
    // Get category IDs
    const category_ids = apiProduct.categories
        .map((categoryURL) => {
            const groups = isoProductCategoryAPIURL
                .unwrap(categoryURL)
                .match(/\/(\d+)\/$/);
            return groups
                ? isoProductCategoryID.wrap(parseInt(groups[1], 10))
                : null;
        })
        .filter(notEmpty);
    // Build children first (will sort after building full product)
    const unsortedChildren = apiProduct.children.map(convertProductFromAPI);
    // Build output product
    const product: InternalProduct = {
        ...apiProduct,
        product_class_slug: productClassSlug,
        rating: rating,
        images: images,
        parent: parent,
        children: unsortedChildren,
        skus: apiProduct.skus || [],
        category_ids: category_ids,
        enable_predicted_delivery_date:
            apiProduct.enable_predicted_delivery_date || false,
    };
    // Sort the product's children
    const sortedChildren = sortProductVariants(product);
    return {
        ...product,
        children: sortedChildren,
    };
};

export const Product = new t.Type<InternalProduct, InternalProduct, unknown>(
    "Product",
    InternalProduct.is,
    (val, context) => {
        return pipe(
            APIProduct.validate(val, context),
            chain((apiProduct) => {
                try {
                    const product = convertProductFromAPI(apiProduct);
                    return t.success(product);
                } catch {
                    return t.failure(val, context);
                }
            }),
        );
    },
    (data) => data,
);
export type IProduct = t.TypeOf<typeof Product>;

export const Products = t.readonlyArray(Product);

// ============================================================================
// Mattress Matches
// ============================================================================
export type IMattressMatchPreferenceSet = Readonly<{
    size: string;
    sleep_position: string;
    height: string;
    weight: string;
    cooling: boolean;
    price_level: string;
}>;

const MattressMatchResultFeel = t.readonly(
    t.type({
        name: t.string,
        icon_url: nullable(t.string),
    }),
);

const MattressMatchResult = t.readonly(
    t.type({
        product: Product,
        copy: t.string,
        image_url: t.string,
        feel: nullable(MattressMatchResultFeel),
        order: t.number,
    }),
);
export type IMattressMatchResult = t.TypeOf<typeof MattressMatchResult>;

// Ensure that the array always contains exactly two items
export const MattressMatchResults = t.readonly(
    t.tuple([MattressMatchResult, MattressMatchResult]),
);
export type IMattressMatchResults = t.TypeOf<typeof MattressMatchResults>;

// ============================================================================
// Bundles
// ============================================================================
const BundleGroup = t.readonly(
    t.type({
        id: t.number,
        bundle_type: t.string,
        name: t.string,
        description: t.string,
        headline: t.string,
        image: nullable(ImageURL),
    }),
);
export type IBundleGroup = t.TypeOf<typeof BundleGroup>;

const AbstractConcreteBundle = <T extends t.Mixed>(SuggestedProduct: T) => {
    return t.readonly(
        t.type({
            id: t.number,
            bundle_group: BundleGroup,
            triggering_product: ProductID,
            suggested_products: t.readonlyArray(SuggestedProduct),
        }),
    );
};

const APIConcreteBundle = AbstractConcreteBundle(ProductID);

export const APIConcreteBundles = t.readonlyArray(APIConcreteBundle);

const ConcreteBundle = AbstractConcreteBundle(Product);
export type IConcreteBundle = t.TypeOf<typeof ConcreteBundle>;
export const ConcreteBundles = t.readonlyArray(ConcreteBundle);

const AbstractUserConfigurableBundle = <T extends t.Mixed>(
    SuggestedProduct: T,
) => {
    return t.readonly(
        t.type({
            id: t.number,
            bundle_group: BundleGroup,
            triggering_product: ProductID,
            suggested_range: t.number,
            suggested_range_products: t.readonlyArray(SuggestedProduct),
            quantity: t.number,
        }),
    );
};

const APIUserConfigurableBundle = AbstractUserConfigurableBundle(ProductID);

export const APIUserConfigurableBundles = t.readonlyArray(
    APIUserConfigurableBundle,
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const UserConfigurableBundle = AbstractUserConfigurableBundle(Product);
export type IUserConfigurableBundle = t.TypeOf<typeof UserConfigurableBundle>;

// ============================================================================
// Basket
// ============================================================================
const BasketModificationLogEntry = t.readonly(
    t.type({
        user_id: nullable(t.number),
        user_name: nullable(t.string),
        user_is_staff: nullable(t.boolean),
        total_excl_tax_before: t.string,
        total_excl_tax_after: t.string,
        message: t.string,
        action_time: t.string,
    }),
);

const BasketLinkShippingMethod = t.readonly(
    t.type({
        code: t.string,
        price: t.string,
        name: t.string,
        description: t.string,
    }),
);
export type IBasketLinkShippingMethod = t.TypeOf<
    typeof BasketLinkShippingMethod
>;

const BasketAdvertistingContent = t.readonly(
    t.type({
        content: SafeHTML,
        group_priority: t.number,
        offer_priority: t.number,
    }),
);
export type IBasketLineAdvertistingContent = t.TypeOf<
    typeof BasketAdvertistingContent
>;

const BasketLineDiscount = t.readonly(
    t.type({
        amount: t.string,
        offer_name: t.string,
        offer_description: t.string,
        voucher_name: nullable(t.string),
        voucher_code: nullable(t.string),
    }),
);

const APIBasketLine = t.readonly(
    t.intersection([
        t.type({
            url: BasketLineAPIURL,
            product: Product,
            quantity: t.number,
            attributes: t.readonlyArray(
                t.readonly(
                    t.type({
                        url: t.string,
                        option: t.string,
                        value: t.union([t.string, t.number]),
                    }),
                ),
            ),
            price_currency: t.string,
            modification_log_entries: t.readonlyArray(
                BasketModificationLogEntry,
            ),
            price_excl_tax: t.string,
            price_incl_tax: t.string,
            price_incl_tax_excl_discounts: t.string,
            price_excl_tax_excl_discounts: t.string,
            discount_value: t.string,
            discounts: t.readonlyArray(BasketLineDiscount),
            line_price_excl_tax_incl_discounts: t.string,
            is_tax_known: t.boolean,
            warning: nullable(t.string),
            basket: t.string,
            stockrecord: t.string,
            date_created: t.string,
        }),
        t.partial({
            advertising_content: t.readonlyArray(BasketAdvertistingContent),
            // Move id and parent up to required-props after 2025.05.29 release.
            id: BasketLineID,
            parent: nullable(BasketLineID),
        }),
    ]),
);

const InternalBasketLine = t.readonly(
    t.intersection([
        APIBasketLine,
        t.type({
            bundles: t.readonlyArray(ConcreteBundle),
            shipping_methods: t.readonlyArray(BasketLinkShippingMethod),
        }),
    ]),
);

type InternalBasketLine = t.TypeOf<typeof InternalBasketLine>;

export type IBasketLineDiscount = t.TypeOf<typeof BasketLineDiscount>;

const BasketLine = new t.Type<InternalBasketLine, InternalBasketLine, unknown>(
    "BasketLine",
    InternalBasketLine.is,
    (val, context) => {
        return pipe(
            APIBasketLine.validate(val, context),
            chain((apiBasketLine) => {
                try {
                    const line: InternalBasketLine = {
                        ...apiBasketLine,
                        bundles: [],
                        shipping_methods: [],
                    };
                    return t.success(line);
                } catch {
                    return t.failure(val, context);
                }
            }),
        );
    },
    (data) => data,
);
export type IBasketLine = t.TypeOf<typeof BasketLine>;

const Voucher = t.readonly(
    t.type({
        name: t.string,
        code: t.string,
        start_datetime: t.string,
        end_datetime: t.string,
    }),
);
export type IVoucher = t.TypeOf<typeof Voucher>;

const OfferDiscount = t.readonly(
    t.intersection([
        t.type({
            index: t.number,
            name: t.string,
            short_name: t.string,
            description: t.string,
            amount: t.string,
            is_hidden: t.boolean,
        }),
        // These can be changed to mandatory after the r2024.08.20 release is in prod.
        t.partial({
            freq: t.number,
            desktop_image: nullable(ImageURL),
            mobile_image: nullable(ImageURL),
            benefit_type: nullable(t.string),
            benefit_value: nullable(t.string),
        }),
    ]),
);
export type IOfferDiscount = t.TypeOf<typeof OfferDiscount>;

const VoucherDiscount = t.readonly(
    t.intersection([
        OfferDiscount,
        t.type({
            voucher: Voucher,
        }),
    ]),
);
export type IVoucherDiscount = t.TypeOf<typeof VoucherDiscount>;

export type IDiscount = IOfferDiscount | IVoucherDiscount;

export const Basket = t.readonly(
    t.intersection([
        t.type({
            id: t.number,
            encoded_basket_id: t.string,
            owner: t.string,
            owner_id: t.number,
            last_modified_timestamp: nullable(t.string),
            status: t.string,
            lines: t.readonlyArray(BasketLine),
            url: t.string,
            total_excl_tax: t.string,
            total_excl_tax_excl_discounts: t.string,
            total_incl_tax: t.string,
            total_incl_tax_excl_discounts: t.string,
            total_tax: t.string,
            currency: nullable(t.string),
            offer_discounts: t.readonlyArray(OfferDiscount),
            voucher_discounts: t.readonlyArray(VoucherDiscount),
            offer_post_order_actions: t.readonlyArray(OfferDiscount),
            voucher_post_order_actions: t.readonlyArray(VoucherDiscount),
            is_tax_known: t.boolean,
            tax_details: t.record(t.string, t.number),
            is_frozen: t.boolean,
            vouchers: t.readonlyArray(Voucher),
            merged_from: t.readonlyArray(t.number),
        }),
        t.partial({
            date_created: t.string,
            date_merged: t.string,
            date_submitted: t.string,
            financing_plans: FinancingPlans,
        }),
    ]),
);
export type IBasket = t.TypeOf<typeof Basket>;

const WishListLine = t.readonly(
    t.type({
        list_type: t.string,
        product: Product,
        url: t.string,
    }),
);
export type IWishListLine = t.TypeOf<typeof WishListLine>;

export const WishListLines = t.readonlyArray(WishListLine);

export const BasketLastModifiedTimestamp = t.readonly(
    t.type({
        id: t.number,
        last_modified_timestamp: nullable(t.string),
    }),
);
export type IBasketLastModifiedTimestamp = t.TypeOf<
    typeof BasketLastModifiedTimestamp
>;

export type IAddon = Readonly<{
    productID: IProductID;
    quantity: number;
}>;
